import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Send, Volume2, VolumeX, Square, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, useAuth } from "@/lib/auth";
import { playVoice, stopVoice, streamAriel } from "@/lib/ariel";
import { Gold } from "@/components/temple/MoonRitual";
import { useApp } from "@/components/app/AppKit";

const AUTO_KEY = "tt-ariel-speak";
const OPENERS = {
  gates: ["Where should I begin tonight?", "I feel disconnected from my body.", "Which gate is calling me?", "Guide me through a short breath."],
  quantum: ["How do I begin to feel the field?", "Guide me through Bio-Field Activation.", "How can we connect across distance?", "What are mirror-currents?"],
};

const Bubble = ({ m, i, speaking, onSpeak }) => (
  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} data-testid={`ariel-msg-${i}`} data-role={m.role} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
    <div className={`max-w-[85%] px-4 py-3 text-[15px] leading-relaxed whitespace-pre-line ${m.role === "user" ? "bg-[#1A120E] border border-[#D9B47A]/20 rounded-2xl rounded-br-sm" : "font-display text-[18px] text-[#F2E8DF]"}`}>
      {m.role === "assistant" && m.text && !m.pending && (
        <button data-testid={`ariel-speak-${i}`} onClick={() => onSpeak(i)} className="mb-2 flex items-center gap-1.5 font-tt-mono text-[10px] uppercase tracking-[0.2em] text-[#D9B47A]/80 hover:text-[#D9B47A]">
          {speaking === i ? <><Square className="w-3 h-3" /> Stop</> : <><Volume2 className="w-3.5 h-3.5" /> Hear Ariel</>}
        </button>
      )}
      {m.text ? (m.role === "assistant" ? <Gold text={m.text.replace(/\*\*/g, "*")} /> : m.text) : <Loader2 className="w-4 h-4 animate-spin text-[#D9B47A]" />}
    </div>
  </motion.div>
);

export default function ArielChat() {
  const { user } = useAuth();
  const { key: space, name } = useApp();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [speaking, setSpeaking] = useState(null);
  const [auto, setAuto] = useState(() => localStorage.getItem(AUTO_KEY) === "1");
  const end = useRef(null);
  useEffect(() => {
    api.get("/ariel/history", { params: { space } }).then(({ data }) => setMsgs(Array.isArray(data) ? data : [])).catch(() => {});
    return () => stopVoice();
  }, [space]);
  useEffect(() => {
    end.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [msgs]);
  const speak = async (i, list = msgs) => {
    if (speaking === i) {
      stopVoice();
      setSpeaking(null);
      return;
    }
    setSpeaking(i);
    try {
      await playVoice(list[i].text);
    } catch {
      toast.error("Ariel’s voice couldn’t play just now.");
    }
    setSpeaking((s) => (s === i ? null : s));
  };
  const send = async (q) => {
    const t = (q ?? text).trim();
    if (!t || busy) return;
    setText("");
    setBusy(true);
    const base = [...msgs, { role: "user", text: t }];
    let reply = "";
    setMsgs([...base, { role: "assistant", text: "", pending: true }]);
    try {
      await streamAriel(t, (tok) => {
        reply += tok;
        setMsgs([...base, { role: "assistant", text: reply, pending: true }]);
      }, space);
      const final = [...base, { role: "assistant", text: reply.trim() }];
      setMsgs(final);
      if (auto && reply.trim()) speak(final.length - 1, final);
    } catch (e) {
      setMsgs(base);
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };
  const toggleAuto = () => {
    const v = !auto;
    setAuto(v);
    localStorage.setItem(AUTO_KEY, v ? "1" : "0");
    if (!v) stopVoice();
  };
  const clear = async () => {
    stopVoice();
    await api.delete("/ariel/history", { params: { space } }).catch(() => {});
    setMsgs([]);
  };
  return (
    <main data-testid="ariel-chat" className="max-w-xl mx-auto px-5 pt-[max(2.5rem,calc(env(safe-area-inset-top)+1.5rem))] pb-48 min-h-[100dvh]">
      <div className="flex items-center gap-4">
        <span className="w-14 h-14 rounded-full border border-[#D9B47A]/40 overflow-hidden shadow-[0_0_30px_rgba(217,180,122,0.15)]"><img src="/images/goddess-ariel-avatar.jpg" alt="Goddess Ariel" className="w-full h-full object-cover" /></span>
        <div>
          <p data-testid="ariel-app-name" className="font-tt-mono text-[11px] uppercase tracking-[0.3em] text-[#D9B47A]">{name}</p>
          <h1 className="font-display text-3xl">Goddess <em className="text-[#D9B47A]">Ariel</em></h1>
        </div>
      </div>
      <div className="mt-5 flex items-center gap-4 text-[12px]">
        <button data-testid="ariel-auto-voice" role="switch" aria-checked={auto} onClick={toggleAuto} className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 font-tt-mono uppercase tracking-[0.15em] transition-colors ${auto ? "border-[#D9B47A] text-[#0A0706] bg-[#D9B47A]" : "border-[#D9B47A]/40 text-[#D9B47A]"}`}>
          {auto ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />} Speak replies
        </button>
        {msgs.length > 0 && <button data-testid="ariel-clear" onClick={clear} className="inline-flex items-center gap-1.5 text-[#A8998C] hover:text-[#E8838E]"><Trash2 className="w-3.5 h-3.5" /> Begin anew</button>}
      </div>
      <div className="mt-8 space-y-5">
        {msgs.length === 0 && (
          <div data-testid="ariel-welcome">
            <p className="font-display text-2xl leading-snug">Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}. I am here. Tell me what your body is feeling, or what you are longing for.</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {OPENERS[space].map((o, k) => (
                <button key={o} data-testid={`ariel-opener-${k}`} onClick={() => send(o)} className="rounded-full border border-[#D9B47A]/30 px-4 py-2 text-sm text-[#CFC2B6] hover:border-[#D9B47A] hover:text-[#F2E8DF] transition-colors">{o}</button>
              ))}
            </div>
          </div>
        )}
        {msgs.map((m, i) => <Bubble key={i} m={m} i={i} speaking={speaking} onSpeak={speak} />)}
        <div ref={end} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="fixed inset-x-0 bottom-[calc(64px+env(safe-area-inset-bottom))] z-40 bg-gradient-to-t from-[#0A0706] via-[#0A0706] to-transparent pt-6 pb-3">
        <div className="max-w-xl mx-auto px-5 flex items-end gap-2">
          <textarea data-testid="ariel-input" rows={1} maxLength={1500} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder="Speak to Ariel…" className="flex-1 resize-none max-h-32 bg-[#120C0A] border border-[#D9B47A]/25 rounded-2xl px-4 py-3 text-[15px] placeholder:text-[#8F8176] focus:outline-none focus:border-[#D9B47A]" />
          <button data-testid="ariel-send" disabled={busy || !text.trim()} aria-label="Send" className="w-12 h-12 rounded-full bg-[#D9B47A] text-[#0A0706] flex items-center justify-center disabled:opacity-50 hover:bg-[#F2E8DF] transition-colors">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
        <p className="max-w-xl mx-auto px-5 mt-2 text-[11px] text-[#8F8176]">Ariel’s guidance is for spiritual practice, not medical or therapeutic care.</p>
      </form>
    </main>
  );
}
